import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { createContact, createCampaign } from '../services/api';

const STEPS = ['Welcome', 'Import Contacts', 'Create Campaign', 'Done'];

const CAMPAIGN_TYPES = [
  { value: 'one_time', label: 'One-Time' },
  { value: 'drip', label: 'Drip' },
  { value: 'automated', label: 'Automated' },
  { value: 'product_led_growth', label: 'Product-Led Growth' },
];

const inputStyle: React.CSSProperties = {
  background: 'var(--bg-input)',
  border: '1px solid var(--border-primary)',
  color: 'var(--text-primary)',
};

function focusBorder(e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) {
  e.currentTarget.style.borderColor = 'var(--netrun-green)';
}
function blurBorder(e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) {
  e.currentTarget.style.borderColor = 'var(--border-primary)';
}

export default function OnboardingPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);

  // Contact import state
  const [contactText, setContactText] = useState('');
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [importStatus, setImportStatus] = useState<string>('');
  const [importing, setImporting] = useState(false);

  // Campaign state
  const [campaignName, setCampaignName] = useState('');
  const [campaignType, setCampaignType] = useState('one_time');
  const [campaignStatus, setCampaignStatus] = useState<string>('');
  const [creatingCampaign, setCreatingCampaign] = useState(false);

  const userName = (() => {
    try {
      const token = localStorage.getItem('kog_access_token');
      if (!token) return '';
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.first_name || payload.name || payload.sub || '';
    } catch {
      return '';
    }
  })();

  async function handleImportContacts() {
    const lines = contactText
      .split('\n')
      .map((l) => l.trim())
      .filter(Boolean);

    if (lines.length === 0 && !csvFile) {
      setImportStatus('Paste contacts or select a CSV file first.');
      return;
    }

    setImporting(true);
    setImportStatus('');

    let created = 0;
    let failed = 0;

    // Parse pasted text: "Name, email" per line
    for (const line of lines) {
      try {
        const parts = line.split(',').map((s) => s.trim());
        const namePart = parts[0] || '';
        const emailPart = parts[1] || '';
        const namePieces = namePart.split(/\s+/);
        const first_name = namePieces[0] || namePart;
        const last_name = namePieces.slice(1).join(' ') || '';

        await createContact({
          first_name,
          last_name,
          email: emailPart || undefined,
          source: 'onboarding_import',
        });
        created++;
      } catch {
        failed++;
      }
    }

    // Handle CSV file upload
    if (csvFile) {
      try {
        const text = await csvFile.text();
        const csvLines = text.split('\n').map((l) => l.trim()).filter(Boolean);
        // Skip header if it looks like one
        const start = csvLines[0]?.toLowerCase().includes('name') || csvLines[0]?.toLowerCase().includes('email') ? 1 : 0;
        for (let i = start; i < csvLines.length; i++) {
          try {
            const parts = csvLines[i].split(',').map((s) => s.trim().replace(/^"|"$/g, ''));
            const namePart = parts[0] || '';
            const emailPart = parts[1] || '';
            const namePieces = namePart.split(/\s+/);
            await createContact({
              first_name: namePieces[0] || namePart,
              last_name: namePieces.slice(1).join(' ') || '',
              email: emailPart || undefined,
              source: 'onboarding_csv',
            });
            created++;
          } catch {
            failed++;
          }
        }
      } catch {
        failed++;
      }
    }

    setImporting(false);
    setImportStatus(
      `Imported ${created} contact${created !== 1 ? 's' : ''}${failed > 0 ? `, ${failed} failed` : ''}.`
    );
  }

  async function handleCreateCampaign() {
    if (!campaignName.trim()) {
      setCampaignStatus('Enter a campaign name.');
      return;
    }

    setCreatingCampaign(true);
    setCampaignStatus('');

    try {
      await createCampaign({
        name: campaignName.trim(),
        campaign_type: campaignType,
        status: 'draft',
      });
      setCampaignStatus('Campaign created!');
    } catch {
      setCampaignStatus('Failed to create campaign. You can create one later from the Campaigns page.');
    } finally {
      setCreatingCampaign(false);
    }
  }

  function next() {
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
  }

  function back() {
    setStep((s) => Math.max(s - 1, 0));
  }

  return (
    <div
      className="page-enter min-h-screen flex flex-col items-center justify-center px-4"
      style={{ background: 'var(--bg-primary)' }}
    >
      <div className="w-full max-w-[520px]">
        {/* Step indicator */}
        <div className="flex items-center justify-center gap-2 mb-8">
          {STEPS.map((label, i) => (
            <div key={label} className="flex items-center gap-2">
              <div
                className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-display transition-all duration-300"
                style={{
                  background: i <= step ? 'var(--netrun-green)' : 'var(--bg-input)',
                  color: i <= step ? 'var(--netrun-black)' : 'var(--text-muted)',
                  border: `1px solid ${i <= step ? 'var(--netrun-green)' : 'var(--border-primary)'}`,
                }}
              >
                {i + 1}
              </div>
              {i < STEPS.length - 1 && (
                <div
                  className="w-8 h-[2px] transition-all duration-300"
                  style={{
                    background: i < step ? 'var(--netrun-green)' : 'var(--border-primary)',
                  }}
                />
              )}
            </div>
          ))}
        </div>
        <p
          className="text-center text-xs mb-6"
          style={{ color: 'var(--text-muted)' }}
        >
          Step {step + 1} of {STEPS.length}
        </p>

        {/* Step content */}
        <div
          className="rounded-xl p-8"
          style={{
            background: 'var(--bg-panel)',
            border: '1px solid var(--border-primary)',
          }}
        >
          {/* ── Step 1: Welcome ──────────────────────────────────── */}
          {step === 0 && (
            <div className="text-center space-y-5">
              <h1
                className="font-display text-3xl"
                style={{ color: 'var(--netrun-green)' }}
              >
                Welcome to KOG CRM{userName ? '!' : '!'}
              </h1>
              {userName && (
                <p className="text-lg" style={{ color: 'var(--text-primary)' }}>
                  Hi {userName}, let's get you set up.
                </p>
              )}
              <p className="text-sm leading-relaxed" style={{ color: 'var(--text-secondary)' }}>
                KOG is your all-in-one CRM — contacts, pipeline, campaigns, communications,
                documents, and AI insights in a single workspace.
              </p>
              <div
                className="grid grid-cols-2 gap-3 pt-2 text-xs"
                style={{ color: 'var(--text-secondary)' }}
              >
                {[
                  ['Contacts & Pipeline', 'Manage your network and track deals'],
                  ['Campaigns', 'Multi-stage outreach with analytics'],
                  ['SMS, Voice & Email', 'Reach contacts on any channel'],
                  ['AI Chat', 'Kogsworth — your AI assistant'],
                  ['Documents & RAG', 'Search across your knowledge base'],
                  ['Analytics', 'Dashboards, funnels, and scoring'],
                ].map(([title, desc]) => (
                  <div
                    key={title}
                    className="rounded-lg p-3 text-left"
                    style={{ background: 'var(--bg-input)', border: '1px solid var(--border-primary)' }}
                  >
                    <div className="font-display mb-1" style={{ color: 'var(--text-accent)' }}>
                      {title}
                    </div>
                    <div style={{ color: 'var(--text-muted)' }}>{desc}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── Step 2: Import Contacts ─────────────────────────── */}
          {step === 1 && (
            <div className="space-y-5">
              <h2
                className="font-display text-xl text-center"
                style={{ color: 'var(--text-primary)' }}
              >
                Import Your Contacts
              </h2>
              <p className="text-sm text-center" style={{ color: 'var(--text-secondary)' }}>
                Paste contacts below (one per line: Name, Email) or upload a CSV.
              </p>

              <textarea
                rows={6}
                value={contactText}
                onChange={(e) => setContactText(e.target.value)}
                placeholder={'John Doe, john@example.com\nJane Smith, jane@example.com'}
                className="w-full px-4 py-3 rounded-lg text-sm outline-none transition-all resize-none"
                style={inputStyle}
                onFocus={focusBorder}
                onBlur={blurBorder}
              />

              <div>
                <label
                  className="block text-xs mb-1.5"
                  style={{ color: 'var(--text-secondary)' }}
                >
                  Or upload a CSV file
                </label>
                <input
                  type="file"
                  accept=".csv"
                  onChange={(e) => setCsvFile(e.target.files?.[0] || null)}
                  className="w-full text-sm file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-display file:cursor-pointer"
                  style={{
                    color: 'var(--text-secondary)',
                  }}
                />
              </div>

              {importStatus && (
                <p
                  className="text-xs px-3 py-2 rounded-lg"
                  style={{
                    background: importStatus.includes('failed')
                      ? 'rgba(239,68,68,0.1)'
                      : 'rgba(34,197,94,0.1)',
                    color: importStatus.includes('failed') ? 'var(--warning)' : 'var(--success)',
                    border: `1px solid ${importStatus.includes('failed') ? 'rgba(239,68,68,0.3)' : 'rgba(34,197,94,0.3)'}`,
                  }}
                >
                  {importStatus}
                </p>
              )}

              <button
                onClick={handleImportContacts}
                disabled={importing || (!contactText.trim() && !csvFile)}
                className="w-full py-3 rounded-lg text-sm font-display cursor-pointer transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed"
                style={{
                  background: 'var(--netrun-green)',
                  color: 'var(--netrun-black)',
                  border: 'none',
                }}
              >
                {importing ? 'Importing...' : 'Import Contacts'}
              </button>
            </div>
          )}

          {/* ── Step 3: Create Campaign ─────────────────────────── */}
          {step === 2 && (
            <div className="space-y-5">
              <h2
                className="font-display text-xl text-center"
                style={{ color: 'var(--text-primary)' }}
              >
                Create Your First Campaign
              </h2>
              <p className="text-sm text-center" style={{ color: 'var(--text-secondary)' }}>
                Set up a campaign to reach out to your contacts. You can always do this later.
              </p>

              <div>
                <label className="block text-xs mb-1.5" style={{ color: 'var(--text-secondary)' }}>
                  Campaign Name
                </label>
                <input
                  type="text"
                  value={campaignName}
                  onChange={(e) => setCampaignName(e.target.value)}
                  placeholder="e.g. Q1 Outreach"
                  className="w-full px-4 py-3 rounded-lg text-sm outline-none transition-all"
                  style={inputStyle}
                  onFocus={focusBorder}
                  onBlur={blurBorder}
                />
              </div>

              <div>
                <label className="block text-xs mb-1.5" style={{ color: 'var(--text-secondary)' }}>
                  Campaign Type
                </label>
                <select
                  value={campaignType}
                  onChange={(e) => setCampaignType(e.target.value)}
                  className="w-full px-4 py-3 rounded-lg text-sm outline-none transition-all cursor-pointer"
                  style={inputStyle}
                  onFocus={focusBorder}
                  onBlur={blurBorder}
                >
                  {CAMPAIGN_TYPES.map((t) => (
                    <option key={t.value} value={t.value}>
                      {t.label}
                    </option>
                  ))}
                </select>
              </div>

              {campaignStatus && (
                <p
                  className="text-xs px-3 py-2 rounded-lg"
                  style={{
                    background: campaignStatus.includes('Failed')
                      ? 'rgba(239,68,68,0.1)'
                      : 'rgba(34,197,94,0.1)',
                    color: campaignStatus.includes('Failed') ? 'var(--error)' : 'var(--success)',
                    border: `1px solid ${campaignStatus.includes('Failed') ? 'rgba(239,68,68,0.3)' : 'rgba(34,197,94,0.3)'}`,
                  }}
                >
                  {campaignStatus}
                </p>
              )}

              <button
                onClick={handleCreateCampaign}
                disabled={creatingCampaign || !campaignName.trim()}
                className="w-full py-3 rounded-lg text-sm font-display cursor-pointer transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed"
                style={{
                  background: 'var(--netrun-green)',
                  color: 'var(--netrun-black)',
                  border: 'none',
                }}
              >
                {creatingCampaign ? 'Creating...' : 'Create Campaign'}
              </button>
            </div>
          )}

          {/* ── Step 4: Done ────────────────────────────────────── */}
          {step === 3 && (
            <div className="text-center space-y-5">
              <div
                className="w-16 h-16 rounded-full mx-auto flex items-center justify-center text-2xl"
                style={{
                  background: 'rgba(144, 185, 171, 0.15)',
                  border: '2px solid var(--netrun-green)',
                  color: 'var(--netrun-green)',
                }}
              >
                &#10003;
              </div>
              <h2
                className="font-display text-2xl"
                style={{ color: 'var(--netrun-green)' }}
              >
                You're All Set!
              </h2>
              <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                Your workspace is ready. Jump into any section to get started.
              </p>

              <div className="grid gap-3 pt-2">
                {[
                  { label: 'Go to Dashboard', path: '/' },
                  { label: 'View Contacts', path: '/contacts' },
                  { label: 'View Campaigns', path: '/campaigns' },
                ].map((link) => (
                  <button
                    key={link.path}
                    onClick={() => navigate(link.path)}
                    className="w-full py-3 rounded-lg text-sm font-display cursor-pointer transition-all duration-200"
                    style={{
                      background: link.path === '/' ? 'var(--netrun-green)' : 'var(--bg-input)',
                      color: link.path === '/' ? 'var(--netrun-black)' : 'var(--text-primary)',
                      border: `1px solid ${link.path === '/' ? 'var(--netrun-green)' : 'var(--border-primary)'}`,
                    }}
                  >
                    {link.label}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Navigation buttons */}
        {step < 3 && (
          <div className="flex justify-between mt-6">
            {step > 0 ? (
              <button
                onClick={back}
                className="px-6 py-2.5 rounded-lg text-sm font-display cursor-pointer transition-all duration-200"
                style={{
                  background: 'var(--bg-input)',
                  color: 'var(--text-primary)',
                  border: '1px solid var(--border-primary)',
                }}
              >
                Back
              </button>
            ) : (
              <div />
            )}

            <div className="flex gap-3">
              {step > 0 && step < 3 && (
                <button
                  onClick={next}
                  className="px-6 py-2.5 rounded-lg text-sm font-display cursor-pointer transition-all duration-200"
                  style={{
                    background: 'transparent',
                    color: 'var(--text-muted)',
                    border: '1px solid var(--border-primary)',
                  }}
                >
                  Skip
                </button>
              )}
              <button
                onClick={next}
                className="px-6 py-2.5 rounded-lg text-sm font-display cursor-pointer transition-all duration-200"
                style={{
                  background: 'var(--netrun-green)',
                  color: 'var(--netrun-black)',
                  border: 'none',
                }}
              >
                {step === 0 ? 'Get Started' : 'Next'}
              </button>
            </div>
          </div>
        )}

        {/* Footer */}
        <p
          className="text-center text-xs mt-8"
          style={{ color: 'var(--text-muted)' }}
        >
          Powered by Netrun Systems
        </p>
      </div>
    </div>
  );
}
